/**
 * Builds schema.org JSON-LD for blog post pages.
 *
 * Emits BlogPosting + BreadcrumbList, plus FAQPage when the post body
 * contains an FAQ section (an <h2> matching /frequently asked questions/i
 * followed by <h3> question headings).
 *
 * The Organization and WebSite nodes live in public/index.html and are
 * referenced here by @id rather than duplicated.
 */

const BASE = 'https://marsresume.com';
const ORG_ID = `${BASE}/#organization`;
const SITE_ID = `${BASE}/#website`;

/** Google truncates headlines past ~110 characters. */
const MAX_HEADLINE = 110;

const clean = (str) => (str || '').replace(/\s+/g, ' ').trim();

const parseBody = (html) => {
  if (!html || typeof DOMParser === 'undefined') return null;
  // DOMParser builds an inert document — scripts do not execute.
  return new DOMParser().parseFromString(html, 'text/html').body;
};

/**
 * Pulls question/answer pairs out of a post's FAQ section.
 * Questions are <h3> headings; the answer is every element after it
 * until the next heading. Returns [] when there is no FAQ section.
 */
export const extractFaqs = (html) => {
  const body = parseBody(html);
  if (!body) return [];

  const headings = Array.from(body.querySelectorAll('h2'));
  const faqHeading = headings.find(h => /frequently asked questions|^faqs?$/i.test(clean(h.textContent)));
  if (!faqHeading) return [];

  const faqs = [];
  let node = faqHeading.nextElementSibling;

  while (node && node.tagName !== 'H2') {
    if (node.tagName === 'H3') {
      const question = clean(node.textContent);
      const answerParts = [];

      let answerNode = node.nextElementSibling;
      while (answerNode && !/^H[23]$/.test(answerNode.tagName)) {
        const text = clean(answerNode.textContent);
        if (text) answerParts.push(text);
        answerNode = answerNode.nextElementSibling;
      }

      const answer = answerParts.join(' ');
      if (question && answer) faqs.push({ question, answer });
      node = answerNode;
      continue;
    }
    node = node.nextElementSibling;
  }

  return faqs;
};

/** Approximate word count of the rendered article text. */
const countWords = (html) => {
  const body = parseBody(html);
  const text = clean(body ? body.textContent : '');
  return text ? text.split(' ').length : 0;
};

/**
 * @param {object} post  A published Blog document from GET /blog/slug/:slug
 * @returns {object[]}   JSON-LD nodes to inject on the post page
 */
export const buildBlogPostSchema = (post) => {
  if (!post?.slug) return [];

  const url = `${BASE}/blog/${post.slug}`;
  const published = post.publishedAt || post.createdAt;
  const nodes = [];

  const article = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    '@id': `${url}#article`,
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    url,
    headline: clean(post.title).slice(0, MAX_HEADLINE),
    name: clean(post.title),
    description: clean(post.excerpt),
    inLanguage: 'en-US',
    isPartOf: { '@id': SITE_ID },
    publisher: { '@id': ORG_ID },
    // Posts are written and reviewed by the team, not a single named byline.
    author: { '@type': 'Organization', name: post.author || 'MarsResume Team', url: BASE },
  };

  if (post.coverImage) article.image = [post.coverImage];
  if (published) article.datePublished = new Date(published).toISOString();
  // updatedAt only advances on real content edits — the view counter is
  // written with timestamps disabled so it cannot fake freshness here.
  if (post.updatedAt || published) {
    article.dateModified = new Date(post.updatedAt || published).toISOString();
  }
  if (post.category) article.articleSection = post.category;
  if (post.tags?.length) article.keywords = post.tags.join(', ');

  const wordCount = countWords(post.content);
  if (wordCount) article.wordCount = wordCount;

  nodes.push(article);

  nodes.push({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    '@id': `${url}#breadcrumb`,
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE}/` },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: `${BASE}/blog` },
      { '@type': 'ListItem', position: 3, name: clean(post.title), item: url },
    ],
  });

  const faqs = extractFaqs(post.content);
  // Google wants a meaningful set of Q&As, not a single stray heading.
  if (faqs.length >= 2) {
    nodes.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': `${url}#faq`,
      mainEntity: faqs.map(({ question, answer }) => ({
        '@type': 'Question',
        name: question,
        acceptedAnswer: { '@type': 'Answer', text: answer },
      })),
    });
  }

  return nodes;
};

/** Blog index page: a Blog node listing the most recent posts. */
export const buildBlogIndexSchema = (posts = []) => [{
  '@context': 'https://schema.org',
  '@type': 'Blog',
  '@id': `${BASE}/blog#blog`,
  url: `${BASE}/blog`,
  name: 'MarsResume Career Blog',
  description: 'Resume tips, ATS optimization guides, and career advice from the MarsResume team.',
  inLanguage: 'en-US',
  isPartOf: { '@id': SITE_ID },
  publisher: { '@id': ORG_ID },
  blogPost: posts.slice(0, 20).map(p => ({
    '@type': 'BlogPosting',
    '@id': `${BASE}/blog/${p.slug}#article`,
    url: `${BASE}/blog/${p.slug}`,
    headline: clean(p.title).slice(0, MAX_HEADLINE),
    description: clean(p.excerpt),
    ...(p.coverImage ? { image: [p.coverImage] } : {}),
    ...(p.publishedAt ? { datePublished: new Date(p.publishedAt).toISOString() } : {}),
    author: { '@type': 'Organization', name: p.author || 'MarsResume Team', url: BASE },
  })),
}];
